/**
 * Eingabe-Schemas für persistierte Zuschlags-Regelwerke (OPS-69).
 *
 * Spiegelt die Engine-Typen (`engine/rules.ts`) als Zod-Validierung für die
 * Staff-API. Die Engine-Invarianten (eindeutige Keys, reine Basis, gültige
 * Zeiten) prüft zusätzlich `validateRuleSet` beim Persistieren — dieses
 * Schema fängt die Formfehler vorher ab.
 */
import { z } from 'zod';

/** Lokale Uhrzeit "HH:MM" — Feinprüfung (24:00-Grenze) macht `parseLocalTime`. */
export const localTimeSchema = z
  .string()
  .regex(/^\d{1,2}:\d{2}$/, 'Lokale Uhrzeit im Format "HH:MM"');

export const isoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum im Format YYYY-MM-DD');

export const surchargeConditionSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('always') }),
  z.object({
    kind: z.literal('weekdays'),
    days: z.array(z.number().int().min(0).max(6)).min(1).max(7),
  }),
  z.object({ kind: z.literal('holiday') }),
  z.object({
    kind: z.literal('special_days'),
    days: z
      .array(
        z.object({
          month: z.number().int().min(1).max(12),
          day: z.number().int().min(1).max(31),
          fromLocalTime: localTimeSchema.optional(),
        }),
      )
      .min(1),
  }),
]);

export const surchargeRuleInputSchema = z.object({
  key: z.string().trim().min(1).max(100),
  window: z.object({ from: localTimeSchema, to: localTimeSchema }).optional(),
  condition: surchargeConditionSchema,
  percent: z.number().finite().min(0).max(1000),
  onlyIfShiftStartedBeforeMidnight: z.boolean().optional(),
});

export const surchargeRuleSetCreateSchema = z.object({
  name: z.string().trim().min(1).max(120),
  base: z.enum(['wage', 'bill_rate']),
  stackingPolicy: z.enum(['cumulative', 'highest_only']),
  /** Kundenspezifisches Regelwerk; fehlt = global (Lohnseite/Tarif). */
  clientId: z.string().uuid().optional(),
  validFrom: isoDateSchema,
  notes: z.string().trim().max(2000).optional(),
  rules: z.array(surchargeRuleInputSchema).min(1).max(100),
});

export type SurchargeRuleInput = z.infer<typeof surchargeRuleInputSchema>;
export type SurchargeRuleSetCreateInput = z.infer<typeof surchargeRuleSetCreateSchema>;
